import type { ResourceCard, SortValue } from "./types";
import { TYPE_PRIORITY } from "./collections";

/**
 * Sort rule, ported from the source project:
 *
 *   1. Cards with NO curated position (`manualOrder === null`) come first,
 *      newest `originalPublishDate` first, `TYPE_PRIORITY` breaking exact ties.
 *   2. Cards WITH a curated position follow, by ascending position.
 *
 * With no ordering source wired up, every card is in group 1 — so today this
 * is plain "newest first". The two-group rule is kept because it is what makes
 * a curated order usable in practice: resources published after the curation
 * snapshot surface at the top instead of silently landing at the bottom.
 *
 * `sort` flips group 1 only. **Curated cards keep their ascending order in
 * both directions, deliberately.** A curated position is an editorial decision
 * about what belongs at the top; "oldest first" is a statement about
 * publication date, and reversing a hand-picked running order because a
 * visitor asked for older content first would be reading intent into it that
 * nobody expressed. Group 1 also keeps `TYPE_PRIORITY` as its tie-break in
 * both directions, so exact-timestamp ties stay stable rather than flipping.
 */
/**
 * The timestamp group 1 sorts on: the CMS's editorial date, falling back to the
 * site-publish timestamp when a card has none.
 *
 * The fallback is what keeps the grid sane while the CMS is mid-migration, but
 * it is only coherent when a collection is all-or-nothing. If some cards carry
 * an editorial date and others don't, the two scales mix: `lastPublished` is
 * rewritten for every item on each site publish, so it is always ~now, and a
 * dateless card therefore outranks every dated one. Either backfill the field
 * for a collection or leave it empty — a half-filled collection sorts wrong in
 * a way that looks like a sorting bug rather than missing data.
 */
function sortTime(card: ResourceCard): number {
  const t = Date.parse(card.originalPublishDate ?? card.lastPublished);
  return Number.isNaN(t) ? 0 : t;
}

export function makeResourceComparator(
  sort: SortValue = "latest",
): (a: ResourceCard, b: ResourceCard) => number {
  const direction = sort === "oldest" ? -1 : 1;

  return function compare(a: ResourceCard, b: ResourceCard): number {
    const aHasOrder = a.manualOrder !== null;
    const bHasOrder = b.manualOrder !== null;

    if (!aHasOrder && !bHasOrder) {
      const dateDiff = (sortTime(b) - sortTime(a)) * direction;
      if (dateDiff !== 0) return dateDiff;
      return TYPE_PRIORITY[a.type] - TYPE_PRIORITY[b.type];
    }

    if (!aHasOrder) return -1;
    if (!bHasOrder) return 1;

    return (a.manualOrder as number) - (b.manualOrder as number);
  };
}

/**
 * The default ordering, used for the one sort SSR performs. The island
 * re-sorts client-side when a visitor picks a different direction.
 */
export const compareResources = makeResourceComparator("latest");
